import { useState } from "react";
import { Coins } from "lucide-react";
import { checkoutSubscription } from "../../api/payments.api";
import useSubscriptionStore from "../../store/subscription.store";
import useBillingGate from "../billing/useBillingGate";
import { formatMoney, planDisplayPrice, priceBreakdown } from "../billing/formatGstPrice";

// Frontend mirror of monthly Zencredit grants per plan. Backend is the
// authoritative source (Plan.monthly_credits in subscriptions/migrations/
// 0011_pricing_v2_tiers); this fallback only fires if the API response
// omits monthly_credits. Keep both in sync if either changes.
const PLAN_CREDITS_FALLBACK = {
  TRIAL: 100,            // one-time grant; recurring credits = 0
  B2C_BASIC: 300,
  B2C_INTERMEDIATE: 700,
  B2C_ELITE: 1000,       // T208 — pro-freelancer tier
  B2C_ADVANCED: 1500,
  B2B_CONTACT_SALES: 0,  // no credit gate; AI features bundled in contract
};

export default function PlanCard({ plan, currentPlan, cycle = "monthly" }) {
  const fetchMySubscription = useSubscriptionStore(
    (s) => s.fetchMySubscription,
  );
  const { ensureProfile: ensureBilling, Modal: BillingModal } = useBillingGate();

  const [loading, setLoading] = useState(false);

  const isCurrentPlan = currentPlan?.code === plan.code;
  // Highlight the middle-tier in pricing v2 — same convention as the
  // landing page (Intermediate has popular: true).
  const isPopular = plan.code === "B2C_INTERMEDIATE";
  // T208: ELITE gets its own "FOR PROS" badge so it doesn't look
  // like a "weaker than Advanced" middle tier at a glance — the
  // value is in the perks (marketplace listing + verified badge),
  // not the credits. Distinct visual treatment vs Popular.
  const isElite = plan.code === "B2C_ELITE";
  // Contact-sales gate = the plan explicitly has no published price. INR
  // plans put the price on `price_inr`; foreign-currency plans (Sprint 4
  // Airwallex) put it on `price_minor`. Only treat the plan as contact-
  // sales when BOTH are null AND the code isn't TRIAL. Without checking
  // price_minor, every USD/GBP/AUD/EUR plan falls into the mailto: path
  // even though it has a real published price.
  const isContactSales =
    plan.code === "B2B_CONTACT_SALES"
    || (plan.price_inr == null
        && plan.price_minor == null
        && plan.code !== "TRIAL");

  const handleSubscribe = async () => {
    if (loading) return;
    if (isContactSales) {
      window.location.href =
        "mailto:a.manivannan@sportingzenminds.com?subject=ZenCricketData Organization enquiry";
      return;
    }
    // 2026-06-20 — billing details gate. Subscriptions issue a recurring
    // tax invoice each cycle; we need state + address (and optional GSTIN
    // for ITC) before kicking off the Cashfree mandate.
    const proceed = await ensureBilling({ purpose: "subscribe" });
    if (!proceed) return;
    setLoading(true);

    // 1. Backend mints a PENDING Subscription row + a Cashfree subscription,
    //    returns the hosted mandate-authorisation URL.
    // T217 / P0-5: clean returnUrl strips any stale hash/query so the
    // post-Cashfree landing is deterministic. Without this, deep-links
    // like #plan-X carry through and can break the verification path.
    const cleanReturnUrl = window.location.origin + window.location.pathname;
    // 2026-06-11 — backend now ships YEARLY Plan rows (migration 0022).
    // When the cycle toggle is on Annual, subscribe to the *_ANNUAL
    // variant of this card's monthly plan. The annual plan's price_inr
    // is monthly*10 and Cashfree gets a YEAR cadence; nothing for the
    // operator to do.
    const isAnnualSubscribe = cycle === "annual";
    const targetPlanCode = (
      isAnnualSubscribe && plan.code && !plan.code.endsWith("_ANNUAL")
        ? `${plan.code}_ANNUAL`
        : plan.code
    );
    let res;
    try {
      res = await checkoutSubscription(targetPlanCode, cleanReturnUrl);
    } catch (e) {
      setLoading(false);
      const errCode = e?.response?.data?.code;
      const detail = e?.response?.data?.detail;
      if (errCode === "provider_unavailable") {
        alert(
          "International payments are being activated on our side. " +
          "Please try again in a few days, or email support@zencricketdata.com for assistance."
        );
      } else if (detail) {
        alert(`Could not start subscription: ${detail}`);
      }
      return;
    }
    const { provider, authorisation_url, session_token, subscription_id, order_id } = res.data;
    if (!authorisation_url && !session_token) {
      setLoading(false);
      const providerLabel = provider === "airwallex" ? "Airwallex" : "Cashfree";
      alert(
        `${providerLabel} didn't return an authorisation URL or session. ` +
        `Please retry; if it persists, ask the admin to check ${providerLabel} credentials.`,
      );
      return;
    }

    // T217: stash pending-subscription metadata. Subscriptions page
    // reads this on mount after Cashfree bounces the user back, and
    // verifies whether the mandate actually authorised via the
    // checkout-status endpoint. Without this hand-off, the user lands
    // back on /subscriptions with no signal whether their mandate
    // succeeded, failed, or is still pending.
    try {
      sessionStorage.setItem("zcd_pending_subscription", JSON.stringify({
        plan_code: targetPlanCode,
        plan_name: plan.name,
        subscription_id: subscription_id || null,
        order_id: order_id || null,
        ts: Date.now(),
      }));
    } catch { /* private mode — fine, lose verification */ }

    // T217: brief "Redirecting…" hint before navigation so the operator
    // doesn't think nothing happened during the 200-500ms gap between
    // click and the hosted page appearing.
    setLoading(false);
    const providerLabel = provider === "airwallex" ? "Airwallex" : "Cashfree";
    const banner = document.createElement("div");
    banner.textContent = `Opening ${providerLabel}…`;
    banner.style.cssText = "position:fixed;top:16px;left:50%;transform:translateX(-50%);"
      + "background:#6B1991;color:#fff;padding:8px 16px;border-radius:6px;"
      + "z-index:9999;font:500 13px system-ui";
    document.body.appendChild(banner);

    // 2026-06-29 — Airwallex's hosted Billing Checkout returns a direct
    // URL; no JS SDK to load. Just redirect. The Cashfree path below
    // keeps the V2 session-id + SDK fallback.
    if (provider === "airwallex" && authorisation_url) {
      window.location.assign(authorisation_url);
      return;
    }

    // 2026-06-10 — Subscriptions V2 flow: backend returns either a
    // ready-to-use authorisation_url (older PG path) OR a
    // subscription_session_id (V2 hosted). Prefer the direct URL when
    // present; otherwise load Cashfree's official JS SDK and hand off
    // the session — Cashfree's SDK knows the right hosted-page URL for
    // this merchant config and handles the redirect internally.
    if (authorisation_url) {
      window.location.assign(authorisation_url);
      return;
    }
    try {
      await loadCashfreeSdk();
      const CF = window.Cashfree;
      if (!CF) throw new Error("Cashfree SDK failed to load (window.Cashfree missing)");
      const mode = import.meta.env.PROD ? "production" : "sandbox";
      const cashfree = CF({ mode });
      // 2026-06-10 — Cashfree V3 SDK exposes different methods across
      // versions for subscription checkout. Try the explicit
      // subscriptionsCheckout first; fall back to the generic
      // checkout() which accepts the subscription session id under
      // paymentSessionId (V3 unified flow).
      console.info("[cashfree] sdk loaded, methods=", Object.keys(cashfree));
      // 2026-06-11 — Cashfree V3 SDK subscriptionsCheckout / checkout both
      // return a Promise that RESOLVES with {error?, redirect?, paymentDetails?}
      // even on the failure path — the old `await r` swallowed the error
      // silently. Now we await and inspect. If error is set OR redirect is
      // false-ish, surface the message + try the next strategy.
      let result;
      // 2026-06-11 — Cashfree V3 SDK expects the abbreviated key name
      // 'subsSessionId' (NOT subscriptionSessionId — confirmed by their
      // own error "subsSessionId is missing in options"). We send all
      // three names so any SDK version finds what it expects.
      const baseOpts = {
        subsSessionId: session_token,
        subscriptionSessionId: session_token,
        paymentSessionId: session_token,
      };
      if (typeof cashfree.subscriptionsCheckout === "function") {
        console.info("[cashfree] calling subscriptionsCheckout (_self)");
        result = await cashfree.subscriptionsCheckout({
          ...baseOpts,
          redirectTarget: "_self",
        });
        console.info("[cashfree] subscriptionsCheckout returned:", result);
        if (result && result.error) {
          // Retry once with _blank in case _self was being silently
          // blocked (rare cookie/CSP edge with subscription redirects).
          console.warn("[cashfree] _self failed, retrying _blank:", result.error);
          result = await cashfree.subscriptionsCheckout({
            ...baseOpts,
            redirectTarget: "_blank",
          });
          console.info("[cashfree] _blank returned:", result);
        }
      } else if (typeof cashfree.checkout === "function") {
        console.info("[cashfree] calling checkout (fallback)");
        result = await cashfree.checkout({
          ...baseOpts,
          redirectTarget: "_self",
        });
        console.info("[cashfree] checkout returned:", result);
      } else {
        throw new Error(
          "Cashfree SDK has no subscriptionsCheckout or checkout method. " +
          "Available: " + Object.keys(cashfree).join(", "),
        );
      }
      if (result && result.error) {
        // Build a useful message from whichever shape Cashfree returns
        // (code, message, type are all common keys on result.error).
        const e = result.error;
        const detail =
          e.message || e.description || e.code || JSON.stringify(e);
        throw new Error(`Cashfree rejected the checkout: ${detail}`);
      }
    } catch (e) {
      console.error("[cashfree] checkout failed", e);
      try { document.body.removeChild(banner); } catch { /* */ }
      alert(
        "Couldn't open Cashfree's authorisation page: " +
        (e?.message || "SDK error") +
        ". Open the browser console for details. Retry, or contact support if it persists.",
      );
    }
  };

  // Lazy-load Cashfree's V3 web SDK on demand. Loaded once per page —
  // subsequent calls hit the cached promise instantly. Loading from
  // their CDN keeps our bundle slim and ensures we always have the
  // latest SDK build for whatever URL pattern Cashfree currently uses.
  function loadCashfreeSdk() {
    if (window.Cashfree) return Promise.resolve();
    if (loadCashfreeSdk._p) return loadCashfreeSdk._p;
    loadCashfreeSdk._p = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Cashfree SDK script load failed"));
      document.head.appendChild(s);
    });
    return loadCashfreeSdk._p;
  }

  // Pricing v2 stores price_inr on the Plan row (INR) or currency +
  // price_minor (foreign-currency plans, Sprint 4). planDisplayPrice
  // returns { currency, majorAmount } so the card renders both shapes
  // through one path. Legacy fields kept as fallback for any callers
  // still passing the older plan shape.
  const { currency: planCurrency, majorAmount } = planDisplayPrice(plan);
  const monthlyPrice = (
    majorAmount ??
    plan.price ??
    (plan.amount != null ? plan.amount / 100 : null)
  );
  // Annual price = monthly * 10 (matches Plan migration 0022's pricing
  // — monthly * 10 = 2 months free vs naive 12x). The display
  // multiplier matches the underlying annual Plan row exactly, so the
  // user sees the same amount Cashfree will actually charge after they
  // hit Subscribe on the annual toggle.
  const isAnnual = cycle === "annual";
  const price = (monthlyPrice == null || monthlyPrice === 0)
    ? monthlyPrice
    : (isAnnual ? Math.round(monthlyPrice * 10) : monthlyPrice);
  const isForeignCurrency = planCurrency !== "INR";
  const priceSuffix = (
    plan.billing_cycle === "YEARLY" ? "year"
    : isAnnual ? "year"
    : (plan.billing_cycle ? String(plan.billing_cycle).toLowerCase().replace("ly", "") : "month")
  );
  const storageGb = plan.storage_gb_cap || (plan.storage_gb_per_seat && `${plan.storage_gb_per_seat}/seat`);

  return (
    <div
      className={`relative bg-bg-primary rounded-2xl p-8 border-2 transition-all duration-300 hover:scale-105 ${
        isCurrentPlan
          ? "border-emerald-500 shadow-lg shadow-emerald-500/20"
          : isElite
            ? "border-amber-500 shadow-lg shadow-amber-500/20"
            : isPopular
              ? "border-purple-500 shadow-lg shadow-purple-500/20"
              : "border-bg-secondary hover:border-bg-secondary/50"
      }`}
    >
      {/* 2026-06-10 — 'MOST POPULAR' badge removed per founder ask.
          isPopular still drives the purple ring styling (so the card
          stays visually weighted) but the text/banner is gone. */}
      {/* T208: ELITE FOR PROS badge — distinct amber treatment so it
          doesn't look like the same kind of "popular" callout. The
          value here is professional perks (marketplace listing +
          verified badge), not just the price ladder position. */}
      {isElite && !isCurrentPlan && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-amber-500 to-yellow-500 text-bg-main text-xs font-bold px-4 py-1 rounded-full shadow-lg">
            ⭐ FOR PROS
          </span>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg flex items-center gap-2">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            CURRENT PLAN
          </span>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-text-primary mb-2">
          {plan.name}
        </h3>
        <div className="flex items-baseline justify-center gap-1">
          {price !== null ? (
            <>
              <span className="text-4xl font-bold text-text-primary">
                {formatMoney(planCurrency, price)}
              </span>
              <span className="text-text-secondary">
                /{priceSuffix}
              </span>
            </>
          ) : (
            <span className="text-2xl font-bold text-text-primary">
              Contact Sales
            </span>
          )}
        </div>
        {/* Tax line. INR plans show the GST breakdown (inclusive vs
            exclusive). Foreign-currency plans show a quiet "export of
            services — no GST" line so the customer understands the
            headline price is what they pay; the invoice's IGST / LUT
            handling lives server-side. */}
        {price !== null && !isForeignCurrency && (() => {
          const pb = priceBreakdown({
            price_inr: price,
            is_tax_inclusive: plan.is_tax_inclusive,
          });
          return (
            <div className="mt-1 text-[11px] text-text-secondary leading-tight">
              <div>{pb.taxLine}</div>
              {pb.totalLabel && (
                <div className="text-text-primary font-medium mt-0.5">
                  {pb.totalLabel}/{priceSuffix}
                </div>
              )}
            </div>
          );
        })()}
        {price !== null && isForeignCurrency && (
          <div className="mt-1 text-[11px] text-text-secondary leading-tight">
            <div>Export of services · no GST</div>
          </div>
        )}
      </div>

      {/* Plan Description */}
      {plan.description && (
        <p className="text-text-secondary text-center mb-4 text-sm">
          {plan.description}
        </p>
      )}

      {/* Monthly Zencredit grant + storage — surfaced separately because
          they're the headline differentiators across the tier ladder. */}
      <div className="space-y-2 mb-6">
        {(() => {
          const credits = plan.monthly_credits ?? PLAN_CREDITS_FALLBACK[plan.code];
          if (credits == null || credits === 0) return null;
          return (
            <div className="flex items-center justify-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <Coins className="w-4 h-4 text-emerald-400" />
              <span className="text-sm">
                <span className="font-semibold text-text-primary tabular-nums">{credits}</span>
                <span className="text-text-secondary"> Zencredits / month</span>
              </span>
            </div>
          );
        })()}
        {storageGb ? (
          <div className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <span className="text-sm">
              <span className="font-semibold text-text-primary tabular-nums">{storageGb}</span>
              <span className="text-text-secondary"> GB storage</span>
            </span>
          </div>
        ) : null}
        {plan.skip_credit_gate ? (
          <div className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <span className="text-sm text-text-secondary">All AI features included</span>
          </div>
        ) : null}
      </div>

      {/* Features List */}
      <div className="space-y-3 mb-8">
        {plan.features?.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg
                className="w-3 h-3 text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <span className="text-text-secondary text-sm">{feature}</span>
          </div>
        )) || (
          // Default features if none provided
          <>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-3 h-3 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-text-secondary text-sm">
                Full access to platform
              </span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-3 h-3 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-text-secondary text-sm">
                Priority support
              </span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-3 h-3 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-text-secondary text-sm">
                Regular updates
              </span>
            </div>
          </>
        )}
      </div>

      {/* Subscribe Button */}
      <button
        onClick={handleSubscribe}
        disabled={loading || isCurrentPlan}
        className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
          isCurrentPlan
            ? "bg-bg-secondary text-text-secondary cursor-not-allowed"
            : loading
              ? "bg-button-primary/50 text-text-primary cursor-wait"
              : "bg-button-primary text-text-primary hover:opacity-90 shadow-lg hover:shadow-xl"
        }`}
        style={!isCurrentPlan && !loading ? { backgroundColor: "#6B1991" } : {}}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : isCurrentPlan ? (
          "Current Plan"
        ) : isContactSales ? (
          "Contact Sales"
        ) : plan.code === "TRIAL" ? (
          "Start free trial"
        ) : (
          "Subscribe Now"
        )}
      </button>

      {/* Billing Info */}
      <p className="text-center text-text-secondary text-xs mt-4">
        Billed {plan.billing_cycle || "monthly"}. Cancel anytime.
      </p>
      {/* T209: UPI Autopay is the default mandate type for INR B2C plans.
          Foreign-currency plans go through Airwallex (cards / wallets /
          local rails), so the UPI hint would mislead. Skip it on
          contact-sales + TRIAL + every non-INR plan. */}
      {!isContactSales && plan.code !== "TRIAL" && !isForeignCurrency && (
        <p className="text-center text-text-secondary text-[10px] mt-1 opacity-80">
          📱 UPI Autopay · faster than card mandates
        </p>
      )}
      {!isContactSales && isForeignCurrency && (
        <p className="text-center text-text-secondary text-[10px] mt-1 opacity-80">
          🌐 Cards · Apple Pay · Google Pay via Airwallex
        </p>
      )}

      <BillingModal />
    </div>
  );
}
